import { Component, computed, DestroyRef, inject, signal, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { finalize } from 'rxjs';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { TableModule, TableLazyLoadEvent } from 'primeng/table';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { SelectModule } from 'primeng/select';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { DatePickerModule } from 'primeng/datepicker';
import { TagModule } from 'primeng/tag';
import { ToggleSwitchModule } from 'primeng/toggleswitch';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService, MenuItem } from 'primeng/api';
import { Menu, MenuModule } from 'primeng/menu';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { PurchaseService } from '../../core/services/purchase.service';
import { SupplierService } from '../../core/services/supplier.service';
import { StoreService } from '../../core/services/store.service';
import { ProductService } from '../../core/services/product.service';
import { InventoryService } from '../../core/services/inventory.service';
import { StoreLiteDto } from '../../core/models/store.models';
import { storePicker } from '../../core/utils/store-picker';
import { ProductLiteDto, AddStockBatchItem } from '../../core/models/product.models';
import { ProductVariantDto } from '../../core/models/variant.models';
import { BarcodeScannerComponent } from '../../shared/components/barcode-scanner/barcode-scanner.component';
import {
  PurchaseListItemDto,
  PurchaseDto,
  SupplierDto,
  PaymentMethod,
  PurchaseItemRequest,
} from '../../core/models/finance.models';
import { MoneyPipe } from '../../shared/pipes/money.pipe';
import { CurrencyService } from '../../core/services/currency.service';
import { ScannedProductRow } from '../../core/models/admin.models';

/**
 * Kirillcha harfni lotincha muqobiliga o'giradi — TAQQOSLASH uchun, ko'rsatish
 * uchun EMAS (`row.scannedName` bu funksiyaga umuman tegmaydi, xom holida
 * qoladi). O'zbekcha kirillga xos harflar (ў, қ, ғ, ҳ) bilan birga ruscha
 * so'zlarda uchraydigan harflar (ц, щ, ъ, ы, ь, э) ham qamrab olingan — nakladnoy
 * ruscha nom bilan yozilgan bo'lishi mumkin.
 */
const CYRILLIC_TO_LATIN: Record<string, string> = {
  ў: "o'",
  қ: 'q',
  ғ: "g'",
  ҳ: 'h',
  а: 'a',
  б: 'b',
  в: 'v',
  г: 'g',
  д: 'd',
  е: 'e',
  ё: 'yo',
  ж: 'j',
  з: 'z',
  и: 'i',
  й: 'y',
  к: 'k',
  л: 'l',
  м: 'm',
  н: 'n',
  о: 'o',
  п: 'p',
  р: 'r',
  с: 's',
  т: 't',
  у: 'u',
  ф: 'f',
  х: 'x',
  ц: 's',
  ч: 'ch',
  ш: 'sh',
  щ: 'sh',
  ъ: '',
  ы: 'i',
  ь: '',
  э: 'e',
  ю: 'yu',
  я: 'ya',
};

function romanize(value: string): string {
  let result = '';
  for (const char of value) result += CYRILLIC_TO_LATIN[char] ?? char;
  return result;
}

/**
 * Nomlarni taqqoslash uchun kanonik shakl.
 *
 * <para>
 * <b>Yozuv turi (skript) birxillashtiriladi.</b> Bitta do'konda katalog lotinda
 * kiritilgan bo'lishi, nakladnoy esa qo'lda kirillda yozilgan bo'lishi odatiy
 * holat — "Сут" va "Sut" bir xil mahsulot, lekin harflari umuman boshqa
 * to'plamdan, shuning uchun ular oldin o'xshashlik darajasida ham past chiqardi.
 * `romanize` ikkalasini ham bitta bazaga tushiradi.
 * </para>
 *
 * <para>
 * <b>O'lcham ajratgichlari ham birxillashtiriladi.</b> "50 x 100" va "50*100" —
 * do'konda bitta narsani anglatadi, lekin rasmda qaysi belgi ishlatilgani
 * tasodifiy. Faqat ikki RAQAM orasidagi belgiga tegiladi — so'zning o'zidagi
 * harflarni buzmaslik uchun.
 * </para>
 */
function normalizeName(value: string | null | undefined): string {
  return (
    romanize((value ?? '').trim().toLowerCase())
      // Lotin-o'zbek tutuq belgisining variantlari (ʻ, ʼ, `, ´) — klaviatura yoki
      // OCR/AI qaysi belgini chiqarishi qat'iy emas, ular hammasi bitta narsa.
      .replace(/[ʻʼ`´]/g, "'")
      // `×` (ko'paytirish belgisi) romanize/lowercase orqali o'zgarmaydi — harf emas.
      .replace(/(\d)\s*[x×*]\s*(\d)/gi, '$1x$2')
      .replace(/\s+/g, ' ')
  );
}

/**
 * Ikki matn orasidagi tahrirlash masofasi (Levenshtein) — qo'shish/o'chirish/
 * almashtirish amallarining eng kam soni.
 */
function levenshtein(a: string, b: string): number {
  const prev = new Array(b.length + 1);
  for (let j = 0; j <= b.length; j++) prev[j] = j;

  for (let i = 1; i <= a.length; i++) {
    let diagonal = prev[0];
    prev[0] = i;
    for (let j = 1; j <= b.length; j++) {
      const temp = prev[j];
      prev[j] = a[i - 1] === b[j - 1] ? diagonal : 1 + Math.min(diagonal, prev[j], prev[j - 1]);
      diagonal = temp;
    }
  }
  return prev[b.length];
}

/** 0..1 — 1 aynan bir xil, 0 umuman o'xshamaydi. Uzunlik farqini ham hisobga oladi. */
function similarity(a: string, b: string): number {
  const maxLen = Math.max(a.length, b.length);
  return maxLen === 0 ? 1 : 1 - levenshtein(a, b) / maxLen;
}

/**
 * O'xshashlik bo'yicha eng yaqin mahsulotni topadi — aniq va qism-satr moslik
 * ishlamagan holatlar uchun so'nggi zaxira (masalan, qo'lyozmada bitta harf xato
 * o'qilgan yoki bo'shliq boshqacha qo'yilgan).
 *
 * <b>Ikki himoya bor, tasodifan emas:</b> xato bog'lanish BOSHQA mahsulotning
 * qoldig'i va tannarxini buzadi, shuning uchun "yetarlicha yaqin"ning o'zi
 * kifoya emas:
 * - `FUZZY_THRESHOLD` — natija shunchaki eng yaxshisi emas, ANIQ ham bo'lishi kerak;
 * - `FUZZY_MARGIN` — ikkinchi eng yaqin nomzod undan yetarlicha ortda qolmasa
 *   (masalan, "Coca Cola 1L" va "Coca Cola 1.5L"), bu ANIQLANMAGAN holat —
 *   tizim tanlamaydi, foydalanuvchi tanlaydi.
 */
const FUZZY_THRESHOLD = 0.82;
const FUZZY_MARGIN = 0.08;

function bestFuzzyMatch(name: string, products: ProductLiteDto[]): ProductLiteDto | null {
  let best: ProductLiteDto | null = null;
  let bestScore = 0;
  let secondScore = 0;

  for (const p of products) {
    const score = similarity(name, normalizeName(p.name));
    if (score > bestScore) {
      secondScore = bestScore;
      bestScore = score;
      best = p;
    } else if (score > secondScore) {
      secondScore = score;
    }
  }

  if (bestScore < FUZZY_THRESHOLD) return null;
  if (bestScore - secondScore < FUZZY_MARGIN) return null;
  return best;
}

interface VariantOption {
  id: string;
  label: string;
  purchasePrice: number;
  sellPrice: number;
}

interface PurchaseRow {
  productId: string | null;
  productVariantId: string | null;
  quantity: number;
  purchasePrice: number;
  newSellPrice: number | null;
  /**
   * Mahsulot BUNDAN KEYIN qaysi valyutada sotiladi. Kirim valyutasidan mustaqil:
   * `$` da olib so'mda sotish mumkin. Bo'sh — o'zgarmaydi.
   */
  newSellCurrency: string | null;
  hasVariants: boolean;
  loadingVariants: boolean;
  variantOptions: VariantOption[];
  currentSellPrice: number | null;
  /**
   * Mahsulotning SOTUV valyutasi. Kelish valyutasidan (`form().currency`) farq
   * qilishi mumkin — `$70` ga olib so'mda sotiladigan mol. Variantlar mahsulotdan
   * meros oladi, shuning uchun mahsulot tanlanganda bir marta belgilanadi.
   */
  currentSellCurrency: string | null;
  /**
   * Mahsulotning KELISH valyutasi. Pul kuzatilmaydigan (stok-only) kirimda hujjat
   * valyutasi umuman yo'q — narx aynan shu valyutada tushuniladi (`AddStockBatchCommand`
   * `AddStockCommand` bilan bir xil qoida: kurs qayta olinmaydi).
   */
  currentPurchaseCurrency: string | null;

  /**
   * Rasmdan (AI) o'qilgan nom. Mahsulot tanlangandan keyin ham ko'rinib turadi —
   * bu DALIL: model nomni xato o'qigan yoki noto'g'ri mahsulotga bog'lagan bo'lsa,
   * foydalanuvchi buni faqat shu matnni tanlangan mahsulot bilan solishtirib sezadi.
   * `null` — qator qo'lda kiritilgan.
   */
  scannedName: string | null;

  /** Moslik darajasi. `null` — qo'lda kiritilgan qator, hisobotga tushmaydi. */
  scanMatch: ScanMatch | null;

  /**
   * Model qatorni qanchalik aniq O'QIGANI (0..1). `scanMatch` dan farqli:
   * u qatorning katalogga qanchalik ishonchli BOG'LANGANINI bildiradi, bu esa
   * rasmdagi harf va raqamlarni qanchalik aniq ajratganini. Ikkalasi mustaqil —
   * xira yozilgan nom to'g'ri mahsulotga tushishi, aniq yozilgani esa katalogda
   * umuman bo'lmasligi mumkin.
   */
  scanConfidence: number | null;
}

/** Rasmdagi qator qanchalik ishonchli bog'langani. */
type ScanMatch = 'barcode' | 'name' | 'guess' | 'none';

/**
 * Shu darajadan pastdagi qator "aniq o'qilmadi" deb belgilanadi.
 *
 * Backenddagi `GeminiProductScanService.LowConfidence` bilan bir xil bo'lishi
 * kerak: o'sha yerda bu chegara kuchliroq modelga o'tishga sabab bo'ladi, ya'ni
 * bu yergacha yetib kelgan past qiymat "kuchli model ham ishonch hosil qilmadi"
 * degani — foydalanuvchi uchun eng muhim ogohlantirish.
 */
const LOW_CONFIDENCE = 0.7;

@Component({
  selector: 'app-purchases',
  standalone: true,
  imports: [
    MoneyPipe,
    CommonModule,
    FormsModule,
    TableModule,
    ButtonModule,
    DialogModule,
    SelectModule,
    InputNumberModule,
    InputTextModule,
    DatePickerModule,
    TagModule,
    ToggleSwitchModule,
    ConfirmDialogModule,
    MenuModule,
    TranslatePipe,
    BarcodeScannerComponent,
  ],
  providers: [ConfirmationService],
  templateUrl: './purchases.component.html',
})
export class PurchasesComponent implements OnInit {
  private svc = inject(PurchaseService);
  private supplierSvc = inject(SupplierService);
  private storeSvc = inject(StoreService);
  private productSvc = inject(ProductService);
  private inventorySvc = inject(InventoryService);
  private confirm = inject(ConfirmationService);
  private translate = inject(TranslateService);
  private destroyRef = inject(DestroyRef);

  // ── Ro'yxat ──────────────────────────────────────────────────────────────
  purchases = signal<PurchaseListItemDto[]>([]);
  totalCount = signal(0);
  loading = signal(false);
  pageSize = 20;
  detailVisible = false;
  detailLoading = false;
  selectedPurchase = signal<PurchaseDto | null>(null);
  private lastLazyEvent: TableLazyLoadEvent = { first: 0, rows: this.pageSize };

  // ── Lookup ma'lumotlar ───────────────────────────────────────────────────
  stores = signal<StoreLiteDto[]>([]);

  /** Bitta do'konli biznesda tanlagichlar yashiriladi — `storePicker` ga qarang. */
  readonly storePick = storePicker(this.stores);

  suppliers = signal<SupplierDto[]>([]);
  products = signal<ProductLiteDto[]>([]);
  /** Forma ro'yxat kelishini kutmasdan ochiladi — tanlagichda spinner ko'rsatiladi. */
  productsLoading = signal(false);

  filterStoreId: string | null = null;

  /**
   * To'lov usullari. `External` — tovar biznes hisoblaridan TASHQARI mablag'ga
   * olingan (egasining cho'ntagi, boshqa manba): ombor oshadi, lekin kassa/karta/bank
   * qoldig'i qimirlamaydi. Busiz faqat ikki yomon variant qolardi — yo soxta pul
   * chiqimi yozish, yo kirimni umuman yozmaslik.
   */
  paymentMethods: { label: string; value: PaymentMethod }[] = [
    { label: 'FINANCE.METHOD.Cash', value: 'Cash' },
    { label: 'FINANCE.METHOD.Card', value: 'Card' },
    { label: 'FINANCE.METHOD.Bank', value: 'Bank' },
    { label: 'FINANCE.METHOD.External', value: 'External' },
  ];

  // ── Dialog holati ─────────────────────────────────────────────────────────
  readonly currencySvc = inject(CurrencyService);

  createVisible = false;
  saving = false;

  /**
   * O'CHIQ (sukut) — bu kirim PULSIZ: yetkazib beruvchi, to'lov usuli, qarz
   * umuman so'ralmaydi, `AddStockBatchCommand` orqali faqat ombor qoldig'i
   * oshadi. YONIQ — hozirgi to'liq "Kirim" hujjati (`CreatePurchaseCommand`):
   * qarzga olingan mol kimdandir olinadi, shuning uchun yetkazib beruvchi va
   * to'lov usuli talab qilinadi.
   */
  trackPayment = false;

  form = signal({
    storeId: null as string | null,
    supplierId: null as string | null,
    /**
     * Sukut YO'Q (`null`) va tanlash SHART. Ilgari `Cash` oldindan turardi —
     * kassadan pul chiqmagan kirimlar ham jimgina naqd deb yozilar, kassa qoldig'i
     * esa borig'idan kam ko'rinardi. Endi har kirimda ongli tanlov qilinadi.
     */
    paymentMethod: null as PaymentMethod | null,
    paidAmount: 0,
    dueDate: null as Date | null,
    notes: '',
    /**
     * Hujjat valyutasi — yetkazib beruvchiga QAYSI pulda to'lanadi.
     * Qatorlardagi narxlar va jami shu valyutada.
     */
    currency: '',
    /**
     * Shu invoys uchun kelishilgan kurs. Bo'sh bo'lsa backend tizimdagi joriy
     * kursni oladi; u ham bo'lmasa xato qaytaradi (kurs taxmin qilinmaydi).
     */
    exchangeRate: null as number | null,
  });

  // signal<PurchaseRow[]> — har bir row update'i yangi array reference beradi
  // → Angular @if (row.hasVariants) reaktiv ishlaydi
  rows = signal<PurchaseRow[]>([]);

  // Jami — rows o'zgarganda avtomatik qayta hisoblanadi
  total = computed(() =>
    this.rows().reduce((sum, r) => sum + (r.quantity || 0) * (r.purchasePrice || 0), 0),
  );

  // ── Lifecycle ─────────────────────────────────────────────────────────────
  ngOnInit() {
    this.storeSvc
      .getAllLite()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => this.stores.set(res.data ?? []));
    this.load(this.lastLazyEvent);
  }

  // ── Ro'yxat yuklash ───────────────────────────────────────────────────────
  load(event: TableLazyLoadEvent) {
    this.lastLazyEvent = event;
    const page = Math.floor((event.first ?? 0) / (event.rows ?? this.pageSize)) + 1;
    this.loading.set(true);
    this.svc
      .getAll(page, event.rows ?? this.pageSize, { storeId: this.filterStoreId ?? undefined })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.purchases.set(res.data?.items ?? []);
          this.totalCount.set(res.data?.totalCount ?? 0);
        },
        complete: () => this.loading.set(false),
      });
  }

  onFilterChange() {
    this.load({ first: 0, rows: this.pageSize });
  }

  // ── Dialog ochish ─────────────────────────────────────────────────────────
  openCreate() {
    this.form.set({
      storeId: this.stores()[0]?.id ?? null,
      supplierId: null,
      paymentMethod: null,
      paidAmount: 0,
      dueDate: null,
      notes: '',
      currency: this.currencySvc.defaultCode(),
      exchangeRate: null,
    });
    this.trackPayment = false;
    this.rows.set([this.emptyRow()]);
    this.supplierSvc
      .getAll()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((res) => this.suppliers.set((res.data ?? []).filter((s) => s.isActive)));
    this.productsLoading.set(true);
    this.productSvc
      .getAllLite(null)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.productsLoading.set(false)),
      )
      .subscribe((res) => this.products.set(res.data ?? []));
    this.createVisible = true;
  }

  private emptyRow(): PurchaseRow {
    return {
      productId: null,
      productVariantId: null,
      quantity: 1,
      purchasePrice: 0,
      newSellPrice: null,
      newSellCurrency: null,
      hasVariants: false,
      loadingVariants: false,
      variantOptions: [],
      currentSellPrice: null,
      currentSellCurrency: null,
      currentPurchaseCurrency: null,
      scannedName: null,
      scanMatch: null,
      scanConfidence: null,
    };
  }

  addRow() {
    this.rows.update((rs) => [...rs, this.emptyRow()]);
  }

  removeRow(i: number) {
    this.rows.update((rs) => {
      const next = rs.filter((_, idx) => idx !== i);
      return next.length ? next : [this.emptyRow()];
    });
  }

  // ── Row o'zgartirish (immutable update → signal reaktiv ishlaydi) ─────────
  private updateRow(index: number, patch: Partial<PurchaseRow>) {
    this.rows.update((rs) => rs.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  }

  // Mahsulot tanlanganda
  onProductChange(index: number) {
    const productId = this.rows()[index].productId;

    // Reset
    this.updateRow(index, {
      productVariantId: null,
      variantOptions: [],
      hasVariants: false,
      loadingVariants: false,
      purchasePrice: 0,
      newSellPrice: null,
      newSellCurrency: null,
      currentSellPrice: null,
      currentSellCurrency: null,
      currentPurchaseCurrency: null,
      // Skanerlangan qatorda mahsulotni FOYDALANUVCHI tanladi — bu taxmindan
      // ishonchliroq, shuning uchun daraja ko'tariladi (bo'shatilsa — tushadi).
      ...(this.rows()[index].scanMatch !== null
        ? { scanMatch: (productId ? 'name' : 'none') as ScanMatch }
        : {}),
    });

    if (!productId) return;

    const product = this.products().find((p) => p.id === productId);
    if (!product) return;

    this.updateRow(index, {
      purchasePrice: product.purchasePrice,
      currentSellPrice: product.sellPrice,
      currentSellCurrency: product.sellCurrency,
      currentPurchaseCurrency: product.purchaseCurrency,
      // Tanlagich mahsulotning joriy valyutasini ko'rsatib tursin.
      newSellCurrency: product.sellCurrency,
    });

    if (!product.hasVariants) return;

    this.loadVariants(index, productId);
  }

  /**
   * Variant matritsasini yuklaydi.
   *
   * `keepPrice` — narx HUJJATDAN o'qilgan (skanerlangan qator). Bunday qatorda
   * yagona variant tanlanganda ham narx variantnikiga almashtirilmaydi:
   * nakladnoydagi haqiqiy kelish narxi katalogdagi eskisidan ustun turadi.
   */
  private loadVariants(index: number, productId: string, keepPrice = false) {
    this.updateRow(index, { hasVariants: true, loadingVariants: true });

    this.productSvc
      .getVariantMatrix(productId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          // Foydalanuvchi so'rov tugashidan oldin boshqa mahsulot tanlagan bo'lishi mumkin.
          if (this.rows()[index]?.productId !== productId) return;

          const options: VariantOption[] = (res.data?.variants ?? [])
            .filter((v: ProductVariantDto) => v.isActive && v.values.length > 0)
            .map((v: ProductVariantDto) => ({
              id: v.id,
              label: v.values.map((x) => x.value).join(' / '),
              purchasePrice: v.effectivePurchasePrice,
              sellPrice: v.effectiveSellPrice,
            }));

          // Default (qiymatsiz) inventory varianti haqiqiy mahsulot varianti emas.
          // Stale yoki eski API javobi kelgan taqdirda ham oddiy mahsulot oqimida qolamiz.
          this.updateRow(index, {
            hasVariants: options.length > 0,
            variantOptions: options,
            loadingVariants: false,
          });

          // Bitta variant bo'lsa — avtomatik tanlash
          if (options.length === 1) {
            this.updateRow(index, {
              productVariantId: options[0].id,
              currentSellPrice: options[0].sellPrice,
              ...(keepPrice ? {} : { purchasePrice: options[0].purchasePrice }),
            });
          }
        },
        error: () => this.updateRow(index, { loadingVariants: false }),
      });
  }

  onProductBarcodeDetected(index: number, barcode: string) {
    const found = this.products().find((product) => product.barcode === barcode);
    if (found) {
      this.selectProductForRow(index, found);
      return;
    }

    this.productSvc
      .getByBarcode(barcode)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (product) => {
          if (!product) {
            this.svc.showWarn(this.translate.instant('PURCHASES.BARCODE_NOT_FOUND', { barcode }));
            return;
          }

          const productLite: ProductLiteDto = {
            id: product.id,
            name: product.name,
            barcode: product.barcode,
            storeTypeId: null,

            hasVariants: product.hasVariants ?? false,
            purchasePrice: product.purchasePrice,
            sellPrice: product.sellPrice,
            sellCurrency: product.sellCurrency,
            purchaseCurrency: product.purchaseCurrency,
          };
          if (!this.products().some((item) => item.id === product.id)) {
            this.products.update((products) => [productLite, ...products]);
          }
          this.selectProductForRow(index, productLite);
        },
      });
  }

  private selectProductForRow(index: number, product: ProductLiteDto) {
    this.updateRow(index, { productId: product.id });
    this.onProductChange(index);
    this.svc.showSuccess(this.translate.instant('PURCHASES.PRODUCT_FOUND', { name: product.name }));
  }

  // Variant tanlanganda
  onVariantChange(index: number) {
    const row = this.rows()[index];
    const variant = row.variantOptions.find((v) => v.id === row.productVariantId);
    if (!variant) return;
    this.updateRow(index, {
      purchasePrice: variant.purchasePrice,
      currentSellPrice: variant.sellPrice,
      newSellPrice: null,
      newSellCurrency: null,
    });
  }

  // Input maydon o'zgarganda (ngModel two-way binding signal bilan ishlamaydi — setter kerak)
  setRowField<K extends keyof PurchaseRow>(index: number, field: K, value: PurchaseRow[K]) {
    this.updateRow(index, { [field]: value } as Partial<PurchaseRow>);
  }

  // ── AI: kirim hujjatini rasmdan o'qish ────────────────────────────────────
  //
  // Alohida oyna YO'Q — natija to'g'ridan-to'g'ri shu formaning qatorlariga
  // tushadi. Sabab: oraliq jadval formadagi jadvalning nusxasi bo'lardi (mahsulot
  // tanlagichi, miqdor, narx — hammasi ikki joyda), foydalanuvchi esa bir xil
  // ishni ikki marta ko'rardi. Tuzatish qayerda bo'lsa, tasdiqlash ham o'sha yerda.
  //
  // Skaner mahsulot YARATMAYDI, faqat mavjudlariga bog'laydi: nakladnoyda sotuv
  // narxi bo'lmaydi (`ProductRules.ValidatePrices` uni talab qiladi), noto'g'ri
  // o'qilgan nom esa katalogda ikkinchi "mahsulot" bo'lib qolardi.

  scanning = signal(false);

  /**
   * "AI bilan to'ldirish" tugmasi bosilganda chiqadigan kichik tanlash menyusi
   * (Kamera / Galereya).
   *
   * <para>
   * Ikkita alohida yashirin `<input type="file">` bor (yuqorida, HTML'da) —
   * bittasida `capture="environment"` bor, ikkinchisida yo'q. Sabab: bitta
   * umumiy inputga `capture` qo'yilsa ba'zi qurilmalarda u galereyani butunlay
   * to'sib faqat kamerani ochadi, qo'yilmasa boshqalarida kamera variantini
   * umuman ko'rsatmaydi. Bu yerda foydalanuvchiga esa bitta tugma va ikkita
   * aniq nomlangan tanlov ko'rsatiladi — qaysi input ishlatilishini ORQADA
   * hal qilamiz.
   * </para>
   */
  scanMenuItems: MenuItem[] = [];

  openScanMenu(
    event: Event,
    menu: Menu,
    cameraInput: HTMLInputElement,
    galleryInput: HTMLInputElement,
  ) {
    this.scanMenuItems = [
      {
        label: this.translate.instant('PURCHASE_SCAN.CAMERA'),
        icon: 'pi pi-camera',
        command: () => cameraInput.click(),
      },
      {
        label: this.translate.instant('PURCHASE_SCAN.GALLERY'),
        icon: 'pi pi-image',
        command: () => galleryInput.click(),
      },
    ];
    menu.toggle(event);
  }

  /**
   * Skanerlash hisoboti — model nimani bog'lay olgani va olmagani.
   *
   * `computed`: foydalanuvchi bog'lanmagan qatorga mahsulot tanlashi bilan
   * raqamlar darhol qayta hisoblanadi. Skanerlanmagan (qo'lda kiritilgan)
   * qatorlar hisobga olinmaydi.
   */
  readonly scanReport = computed(() => {
    const scanned = this.rows().filter((r) => r.scanMatch !== null);
    if (!scanned.length) return null;

    return {
      total: scanned.length,
      matched: scanned.filter((r) => r.productId && r.scanMatch !== 'guess').length,
      guess: scanned.filter((r) => r.scanMatch === 'guess').length,
      unmapped: scanned.filter((r) => !r.productId).length,
      // Qo'lyozma daftarning asosiy ko'rsatkichi: model harflarni yoki raqamlarni
      // to'liq ajrata olmagan qatorlar. Bog'lanishdan MUSTAQIL — to'g'ri mahsulotga
      // tushgan qatorning ham narxi xato o'qilgan bo'lishi mumkin.
      unclear: scanned.filter((r) => r.scanConfidence !== null && r.scanConfidence < LOW_CONFIDENCE)
        .length,
    };
  });

  /** Qator aniq o'qilmadimi — jadvalda sariq bilan belgilash uchun. */
  isUnclear(row: PurchaseRow): boolean {
    return row.scanConfidence !== null && row.scanConfidence < LOW_CONFIDENCE;
  }

  onScanFile(event: Event) {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    // Bir xil faylni qayta tanlash ham `change` bersin.
    input.value = '';
    if (!file || this.scanning()) return;

    this.scanning.set(true);
    this.productSvc
      .scanImport(file)
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => this.scanning.set(false)),
      )
      .subscribe({
        next: (res) => this.applyScan(res.data ?? []),
      });
  }

  /**
   * Skanerlangan qatorlarni formaga qo'shadi.
   *
   * Mavjud qatorlar ALMASHTIRILMAYDI: ko'p varaqli nakladnoyni ketma-ket
   * skanerlash yoki bir qismini qo'lda kiritish normal holat. Faqat to'ldirilmagan
   * bo'sh qatorlar olib tashlanadi.
   */
  private applyScan(scanned: ScannedProductRow[]) {
    if (!scanned.length) {
      this.svc.showWarn(this.translate.instant('PURCHASE_SCAN.NOTHING_FOUND'));
      return;
    }

    const kept = this.rows().filter((r) => r.productId || r.scanMatch !== null);
    const added = scanned.map((row) => this.toRow(row));

    this.rows.set([...kept, ...added]);

    // Variant matritsasi qatorlar joyiga qo'yilgandan KEYIN yuklanadi — indeks
    // shundagina aniq bo'ladi. Narx nakladnoydan, shuning uchun `keepPrice`.
    added.forEach((row, i) => {
      if (row.hasVariants && row.productId) this.loadVariants(kept.length + i, row.productId, true);
    });

    this.svc.showSuccess(this.translate.instant('PURCHASE_SCAN.ADDED', { count: added.length }));
  }

  /**
   * Skanerlangan qatorni mavjud mahsulotga bog'laydi.
   *
   * <para>
   * Tartib ishonchlilik bo'yicha kamayib boradi: barkod → to'liq nom → qism-satr
   * → o'xshashlik (fuzzy). Har bosqich oldingisi hech narsa bermagandagina
   * ishga tushadi va faqat natija YAGONA (yoki fuzzy'da — ANIQ g'olib) bo'lganda
   * mahsulot tanlanadi: ikkita "Coca Cola" orasidan tanlashni tizim qila olmaydi,
   * chunki xato tanlov BOSHQA mahsulotning qoldig'i va tannarxini buzadi — buni
   * foydalanuvchi hal qiladi.
   * </para>
   *
   * <para>
   * <b>Fuzzy bosqich qo'lyozma/nakladnoy uchun.</b> "050 x 500" va "050*500" —
   * bir xil mahsulot, faqat ajratgich belgisi boshqacha (buni `normalizeName`
   * allaqachon birxillashtiradi); bundan tashqari model bitta harfni yoki
   * bo'shliqni boshqacha o'qishi mumkin. Bunday farqlar aniq va qism-satr
   * moslikni ham o'tkazib yuboradi, shuning uchun so'nggi zaxira sifatida
   * o'xshashlik darajasi tekshiriladi.
   * </para>
   */
  private toRow(scanned: ScannedProductRow): PurchaseRow {
    const products = this.products();
    const barcode = scanned.barcode?.trim() || null;
    const name = normalizeName(scanned.name);

    let product: ProductLiteDto | undefined;
    let scanMatch: ScanMatch = 'none';

    if (barcode) {
      product = products.find((p) => p.barcode === barcode);
      if (product) scanMatch = 'barcode';
    }

    if (!product && name) {
      const exact = products.filter((p) => normalizeName(p.name) === name);
      if (exact.length === 1) {
        product = exact[0];
        scanMatch = 'name';
      } else if (exact.length === 0) {
        const partial = products.filter(
          (p) => normalizeName(p.name).includes(name) || name.includes(normalizeName(p.name)),
        );
        if (partial.length === 1) {
          product = partial[0];
          scanMatch = 'guess';
        } else if (partial.length === 0) {
          const fuzzy = bestFuzzyMatch(name, products);
          if (fuzzy) {
            product = fuzzy;
            scanMatch = 'guess';
          }
        }
      }
    }

    return {
      ...this.emptyRow(),
      scannedName: scanned.name,
      scanMatch,
      scanConfidence: scanned.confidence,
      productId: product?.id ?? null,
      // Nakladnoyda miqdor ko'rsatilmagan bo'lsa 1 — bu kirim hujjati, nol miqdorli
      // qator ma'nosiz va foydalanuvchi baribir jadvalda to'g'rilaydi.
      quantity: scanned.quantity > 0 ? scanned.quantity : 1,
      // 0 — "hujjatdan o'qilmadi": katalogdagi tannarx saqlanadi. Aks holda bo'sh
      // qiymat jimgina "0 ga keldi" deb tannarxni buzardi.
      purchasePrice:
        scanned.purchasePrice > 0 ? scanned.purchasePrice : (product?.purchasePrice ?? 0),
      currentSellPrice: product?.sellPrice ?? null,
      currentSellCurrency: product?.sellCurrency ?? null,
      currentPurchaseCurrency: product?.purchaseCurrency ?? null,
      newSellCurrency: product?.sellCurrency ?? null,
      hasVariants: product?.hasVariants ?? false,
    };
  }

  matchClass(match: ScanMatch | null): string {
    if (match === 'barcode' || match === 'name') return 'text-green-600 dark:text-green-400';
    if (match === 'guess') return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  }

  // ── Form yordamchilari ────────────────────────────────────────────────────
  patchForm(patch: Partial<ReturnType<typeof this.form>>) {
    this.form.update((f) => ({ ...f, ...patch }));
  }

  markPaidFull() {
    this.patchForm({ paidAmount: this.total() });
  }

  /**
   * Joriy sotuv narxi — bo'sh qoldirilsa shu narx saqlanadi.
   *
   * `toLocaleString()` ISHLATILMAYDI (loyihada taqiqlangan): u valyutani bilmaydi
   * va `4.4234 $` ni `4,423` qilib ko'rsatardi — foydalanuvchi o'zi kiritmagan
   * narxni ko'rardi. Formatlash faqat `CurrencyService` orqali.
   */
  sellPricePlaceholder(row: PurchaseRow): string | undefined {
    return row.currentSellPrice !== null
      ? this.currencySvc.formatValue(row.currentSellPrice, row.currentSellCurrency)
      : undefined;
  }

  rowTotal(row: PurchaseRow): number {
    return (row.quantity || 0) * (row.purchasePrice || 0);
  }

  // ── Saqlash ───────────────────────────────────────────────────────────────
  save() {
    // Tanlagich yashirilgan bo'lsa qiymat `openCreate()` da qo'yiladi; do'kon
    // ro'yxati kechikib kelgan holat uchun zaxira.
    if (!this.form().storeId) this.patchForm({ storeId: this.storePick.single()?.id ?? null });

    const f = this.form();
    if (!f.storeId) {
      this.svc.showWarn(this.translate.instant('PURCHASES.VALIDATION.STORE'));
      return;
    }

    // Skanerlangan, lekin bog'lanmagan qator JIMGINA tushib qolmasligi kerak:
    // nakladnoyning bir satri yo'qolsa qoldiq noto'g'ri bo'ladi va buni keyin
    // topish deyarli imkonsiz. Kerak bo'lmasa foydalanuvchi qatorni o'chiradi.
    const unmapped = this.rows().filter((r) => r.scanMatch !== null && !r.productId);
    if (unmapped.length) {
      this.svc.showWarn(
        this.translate.instant('PURCHASE_SCAN.BLOCK_UNMAPPED', { count: unmapped.length }),
      );
      return;
    }

    const validRows = this.rows().filter((r) => r.productId && r.quantity > 0);
    if (!validRows.length) {
      this.svc.showWarn(this.translate.instant('PURCHASES.VALIDATION.ITEMS'));
      return;
    }

    if (validRows.some((r) => r.hasVariants && !r.productVariantId)) {
      this.svc.showWarn(this.translate.instant('PURCHASES.VALIDATION.VARIANT'));
      return;
    }

    // Pul kuzatilmaydigan (sukut) holat — faqat ombor qoldig'ini oshiradi.
    // Yetkazib beruvchi, to'lov usuli, qarz UMUMAN so'ralmaydi (§ pulsiz kirim).
    if (!this.trackPayment) {
      this.saveStockOnly(f.storeId, validRows, f.notes);
      return;
    }

    // To'lov usuli oldindan tanlanmaydi — pul qayerdan chiqqani har kirimda
    // boshqacha va taxmin qilingani kassa qoldig'ini buzadi.
    if (!f.paymentMethod) {
      this.svc.showWarn(this.translate.instant('PURCHASES.VALIDATION.METHOD'));
      return;
    }

    // Sotuv valyutasi almashtirilsa yangi narx SHART: narx qiymati o'girilmaydi,
    // ya'ni eski `900 000` jimgina `$900 000` bo'lib qolardi.
    const missingPrice = validRows.find(
      (r) =>
        r.newSellCurrency &&
        r.newSellCurrency !== r.currentSellCurrency &&
        !(r.newSellPrice && r.newSellPrice > 0),
    );
    if (missingPrice) {
      this.svc.showWarn(this.translate.instant('PURCHASES.VALIDATION.SELL_PRICE_REQUIRED'));
      return;
    }

    // Qarzga olingan mol KIMDANDIR olinadi. Yetkazib beruvchisiz qarzni keyin
    // to'lab bo'lmaydi (to'lov faqat yetkazuvchi orqali yoziladi) va u kreditorlik
    // hisobotida ham ko'rinmaydi. To'liq to'langan bo'lsa shart emas.
    if (!f.supplierId && f.paidAmount < this.total()) {
      this.svc.showWarn(this.translate.instant('PURCHASES.VALIDATION.SUPPLIER_REQUIRED_FOR_DEBT'));
      return;
    }

    const items: PurchaseItemRequest[] = validRows.map((r) => ({
      productId: r.productId!,
      productVariantId: r.productVariantId ?? undefined,
      quantity: r.quantity,
      purchasePrice: r.purchasePrice,
      newSellPrice: r.newSellPrice,
      // Faqat HAQIQATAN o'zgartirilgan bo'lsa yuboriladi — aks holda backend
      // "yangi narx shart" xatosini bermasligi kerak.
      newSellCurrency:
        r.newSellCurrency && r.newSellCurrency !== r.currentSellCurrency ? r.newSellCurrency : null,
    }));

    this.saving = true;
    this.svc
      .create({
        storeId: f.storeId,
        items,
        supplierId: f.supplierId,
        paidAmount: f.paidAmount,
        paymentMethod: f.paymentMethod,
        dueDate: f.dueDate ? f.dueDate.toISOString() : null,
        notes: f.notes || null,
        currency: f.currency || null,
        exchangeRate: f.exchangeRate,
      })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.svc.showCreateSuccess();
          this.createVisible = false;
          this.load(this.lastLazyEvent);
        },
        error: () => (this.saving = false),
        complete: () => (this.saving = false),
      });
  }

  /**
   * Pulsiz (stok-only) kirim — `AddStockBatchCommand`. Hujjat valyutasi yo'q:
   * narx har qatorning O'Z kelish valyutasida tushuniladi, kurs qayta olinmaydi
   * (`AddStockCommand` bilan bir xil qoida). Kassa, qarz, kreditorlik hisoboti
   * — hech biri qimirlamaydi.
   */
  private saveStockOnly(storeId: string, validRows: PurchaseRow[], notes: string) {
    const items: AddStockBatchItem[] = validRows.map((r) => ({
      productId: r.productId!,
      quantity: r.quantity,
      productVariantId: r.productVariantId ?? undefined,
      // 0/bo'sh — "narx kiritilmadi" deb qaraladi, tannarx o'zgarmaydi. Aks holda
      // bo'sh maydon jimgina "0 so'mga keldi" deb tannarxni buzardi.
      newPurchasePrice: r.purchasePrice > 0 ? r.purchasePrice : undefined,
      newSellPrice: r.newSellPrice ?? undefined,
    }));

    this.saving = true;
    this.inventorySvc
      .addStockBatch({ storeId, items, description: notes || undefined })
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.svc.showCreateSuccess();
          this.createVisible = false;
          this.load(this.lastLazyEvent);
        },
        error: () => (this.saving = false),
        complete: () => (this.saving = false),
      });
  }

  // ── O'chirish ─────────────────────────────────────────────────────────────
  deletePurchase(p: PurchaseListItemDto) {
    this.confirm.confirm({
      message: this.translate.instant('PURCHASES.DELETE_CONFIRM', { number: p.purchaseNumber }),
      header: this.translate.instant('COMMON.CONFIRM'),
      icon: 'pi pi-exclamation-triangle',
      accept: () =>
        this.svc
          .delete(p.id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe({
            next: () => {
              this.svc.showSuccess();
              this.load(this.lastLazyEvent);
            },
          }),
    });
  }

  openDetails(id: string) {
    this.selectedPurchase.set(null);
    this.detailLoading = true;
    this.detailVisible = true;
    this.svc
      .getById(id)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => this.selectedPurchase.set(res.data ?? null),
        error: () => (this.detailLoading = false),
        complete: () => (this.detailLoading = false),
      });
  }

  closeDetails() {
    this.detailVisible = false;
    this.selectedPurchase.set(null);
  }

  // ── Yetkazuvchisiz qolgan hujjatni tuzatish ───────────────────────────────
  //
  // Yetkazuvchisiz qarzni to'lab bo'lmaydi: to'lov faqat yetkazuvchi orqali
  // yoziladi va kreditorlik hisoboti ham yetkazuvchilar bo'yicha yig'iladi.
  // Yangi hujjatlarda bu holat to'sildi, bu esa eski qatorlar uchun.
  assignVisible = false;
  assignSaving = false;
  assignSupplierId: string | null = null;
  assignTarget = signal<PurchaseListItemDto | null>(null);

  openAssignSupplier(p: PurchaseListItemDto) {
    this.assignTarget.set(p);
    this.assignSupplierId = null;
    this.assignVisible = true;

    // Ro'yxat faqat kirim dialogi ochilganda yuklanadi — bu yerda kafolatlanmagan.
    if (!this.suppliers().length) {
      this.supplierSvc
        .getAll()
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((res) => this.suppliers.set((res.data ?? []).filter((s) => s.isActive)));
    }
  }

  saveAssignSupplier() {
    const target = this.assignTarget();
    if (!target || !this.assignSupplierId) return;

    this.assignSaving = true;
    this.svc
      .assignSupplier(target.id, this.assignSupplierId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.svc.showSuccess(this.translate.instant('PURCHASES.ASSIGN.SUCCESS'));
          this.assignVisible = false;
          this.load(this.lastLazyEvent);
        },
        error: () => (this.assignSaving = false),
        complete: () => (this.assignSaving = false),
      });
  }

  remaining(p: PurchaseListItemDto) {
    return Math.max(0, p.totalAmount - p.paidAmount);
  }
  statusSeverity(s: string): 'success' | 'warn' | 'danger' {
    return s === 'Paid' ? 'success' : s === 'PartiallyPaid' ? 'warn' : 'danger';
  }
}
